/**
 * The AI decision function: choose a turn (nothing / left / right) and whether
 * to spend a held bonus, for one bike this tick.
 *
 * Keeps the 2013 algorithm — a coarse danger grid + BFS distance maps + nearest
 * reachable target (`creerGrilleAnalyse` / `creerGrilleDistances` /
 * `choisieCibleIA`) — but replaces the ~450-line `choisieDirection` if/else tree
 * with a compact scored evaluator over the three legal moves.
 */

import type { Match, PlayerInput } from "../sim/match";
import type { GridBounds } from "../sim/collision";
import { BonusKind, Direction, PlayerState } from "../sim/types";
import type { TurnInput } from "../sim/types";
import type { AiLevel } from "./levels";
import {
  bikeHead,
  buildDanger,
  CELL,
  cellGrid,
  floodFill,
  stepIndex,
  worldToIndex,
  type CellGrid,
} from "./grid";

export interface AiScratch {
  cg: CellGrid;
  danger: Int16Array;
  distSelf: Int16Array;
  distTarget: Int16Array;
  distMove: Int16Array;
  queue: Int32Array;
}

export function makeAiScratch(grid: GridBounds): AiScratch {
  const cg = cellGrid(grid);
  const n = cg.cols * cg.rows;
  return {
    cg,
    danger: new Int16Array(n),
    distSelf: new Int16Array(n),
    distTarget: new Int16Array(n),
    distMove: new Int16Array(n),
    queue: new Int32Array(n),
  };
}

const LEFT_OF: Record<Direction, Direction> = {
  [Direction.None]: Direction.None,
  [Direction.Up]: Direction.Left,
  [Direction.Left]: Direction.Down,
  [Direction.Down]: Direction.Right,
  [Direction.Right]: Direction.Up,
};

const RIGHT_OF: Record<Direction, Direction> = {
  [Direction.None]: Direction.None,
  [Direction.Up]: Direction.Right,
  [Direction.Right]: Direction.Down,
  [Direction.Down]: Direction.Left,
  [Direction.Left]: Direction.Up,
};

/** How many free cells straight ahead of `headIdx`, capped at `cap`. */
function clearAhead(
  danger: Int16Array,
  headIdx: number,
  dir: Direction,
  cg: CellGrid,
  cap: number,
): number {
  for (let s = 1; s <= cap; s++) {
    const idx = stepIndex(headIdx, dir, s, cg);
    if (idx < 0 || danger[idx] !== 0) return s - 1;
  }
  return cap;
}

/**
 * @param random deterministic RNG in [0,1), threaded from the match seed so
 *   AI-driven matches stay replayable.
 */
export function computeAiMove(
  match: Match,
  selfIndex: number,
  level: AiLevel,
  scratch: AiScratch,
  random: () => number,
): PlayerInput {
  const self = match.players[selfIndex]!;
  const bike = self.bike;
  const { grid } = match;
  const { cg, danger, distSelf, distTarget, distMove, queue } = scratch;

  buildDanger(
    {
      grid,
      segments: match.segments,
      bikes: match.players.map((p) => ({
        bike: p.bike,
        alive: p.state === PlayerState.Alive,
        index: p.index,
      })),
      selfIndex,
      selfColor: self.color,
      trailLife: match.config.trailLife,
    },
    cg,
    danger,
  );

  const head = bikeHead(bike);
  const headIdx = worldToIndex(head.x, head.y, grid, cg);
  floodFill(danger, headIdx, cg, distSelf, queue);

  // Nearest reachable live opponent (`choisieCibleIA`).
  let target = -1;
  let targetDist = Infinity;
  for (const p of match.players) {
    if (p.index === selfIndex || p.state !== PlayerState.Alive) continue;
    const oh = bikeHead(p.bike);
    const d = distSelf[worldToIndex(oh.x, oh.y, grid, cg)]!;
    if (d >= 0 && d < targetDist) {
      targetDist = d;
      target = p.index;
    }
  }

  const chasing = target >= 0 && level.chaseWeight > 0;
  if (chasing) {
    const th = bikeHead(match.players[target]!.bike);
    floodFill(danger, worldToIndex(th.x, th.y, grid, cg), cg, distTarget, queue);
  }

  const margin = Math.ceil((bike.speed + 1) / CELL) + level.extraMargin;
  const candidates: Array<{ dir: Direction; move: TurnInput | null; straight: boolean }> = [
    { dir: bike.dir, move: null, straight: true },
    { dir: LEFT_OF[bike.dir], move: LEFT_OF[bike.dir] as TurnInput, straight: false },
    { dir: RIGHT_OF[bike.dir], move: RIGHT_OF[bike.dir] as TurnInput, straight: false },
  ];

  let bestScore = -Infinity;
  let bestMove: TurnInput | null = null;
  const safeMoves: Array<TurnInput | null> = [];
  let straightClear = margin;

  for (const { dir, move, straight } of candidates) {
    if (dir === Direction.None) continue;

    const clear = clearAhead(danger, headIdx, dir, cg, margin);
    const safe = clear >= margin;
    if (straight) straightClear = clear;

    let score: number;
    if (!safe) {
      score = -1e9 + clear * 1000;
    } else {
      safeMoves.push(move);
      const enter = stepIndex(headIdx, dir, 1, cg);
      const space = enter >= 0 ? floodFill(danger, enter, cg, distMove, queue) : 0;
      score = space;
      if (chasing && enter >= 0) {
        const g = distTarget[enter]!;
        if (g >= 0) score += (cg.cols + cg.rows - g) * level.chaseWeight;
      }
      if (straight) score += level.straightBias;
    }

    if (score > bestScore) {
      bestScore = score;
      bestMove = move;
    }
  }

  let turn = bestMove;
  if (level.randomTurnChance > 0 && safeMoves.length > 1 && random() < level.randomTurnChance) {
    turn = safeMoves[Math.floor(random() * safeMoves.length)] ?? bestMove;
  }

  return {
    turn,
    useBonus: wantsBonus(self.effect, straightClear, margin, danger, headIdx, bike.dir, cg),
  };
}

function wantsBonus(
  effect: BonusKind,
  straightClear: number,
  margin: number,
  danger: Int16Array,
  headIdx: number,
  dir: Direction,
  cg: CellGrid,
): boolean {
  // JUMP: panic dodge when the way ahead is blocked within the safety margin.
  if (effect === BonusKind.Jump) return straightClear < margin;
  // BOOST: only when there's a long clear runway ahead.
  if (effect === BonusKind.Boost) return clearAhead(danger, headIdx, dir, cg, 12) >= 12;
  return false;
}
